import type { ContentCardItem } from '@content-search/domain';
import { InlineAudioPlayer } from '@core/components';
import { getContentDisplayText } from '@core/utils';
import {
  Card,
  Text,
  Badge,
  BadgeSize,
  Button,
  ButtonSize,
  ButtonType,
  IconButton,
  IconButtonSize,
  IconSize,
  Eye,
  StatusCircleBlock,
} from '@soteria/design-system-ve';
import { useState } from 'react';

import * as styles from './ContentCard.css';
import * as imageStyles from './ContentCardImage.css';
import { ExpandableDetails } from './ExpandableDetails';
import { PolicyDisplay } from './PolicyDisplay';

// Image thumbnail component
function ImageThumbnail({ imageUrl, altText, isSafe, isContentBlurred }: { imageUrl: string; altText: string; isSafe: boolean; isContentBlurred: boolean }) {
  const [imageError, setImageError] = useState(false);

  if (imageError) {
    return (
      <div aria-label={altText} className={imageStyles.thumbnailContainer} role="img">
        <Text className={imageStyles.thumbnailErrorText}>Image unavailable</Text>
      </div>
    );
  }

  const applyBlur = !isSafe && isContentBlurred;

  return (
    <div className={imageStyles.thumbnailContainer}>
      <img
        alt={altText}
        className={`${imageStyles.thumbnailImage} ${applyBlur ? imageStyles.blurredThumbnail : ''}`}
        src={imageUrl}
        onError={() => setImageError(true)}
      />
    </div>
  );
}

// Component for search-style content summary with black background and colored border
// eslint-disable-next-line complexity
function SearchStyleContentSummary({
  item,
  mediaServiceUrl,
  isContentBlurred,
  onToggleBlur,
}: {
  item: ContentCardItem;
  mediaServiceUrl?: string;
  isContentBlurred: boolean;
  onToggleBlur: (e: React.MouseEvent) => void;
}) {
  // Determine if content is safe or harmful for border color
  const isSafeContent = item.classificationResult !== 'rejected';
  const borderClass = isSafeContent ? styles.safeContentBorder : styles.unsafeContentBorder;
  const hasImage = Boolean(item.contentUrl) && item.contentType?.toLowerCase() === 'image';

  return (
    <div className={`${styles.searchContentSummary} ${hasImage ? styles.searchContentSummaryWithImage : ''} ${borderClass}`}>
      {/* Image thumbnail (left side) */}
      {hasImage && (
        <ImageThumbnail
          altText={`Content image for ${item.subjectId}`}
          imageUrl={item.contentUrl || ''}
          isContentBlurred={isContentBlurred}
          isSafe={isSafeContent}
        />
      )}

      {/* Content column */}
      <div className={hasImage ? imageStyles.contentColumn : ''}>
        {/* Primary Transcription Content */}
        <div className={styles.searchTranscriptionContent}>
          <div className={`${styles.textContainer} ${styles.contentBoxContainer}`}>
            <div className={styles.textWrapper}>
              <Text className={`${styles.searchTranscriptionText} ${!isSafeContent && isContentBlurred ? imageStyles.blurredText : ''}`}>
                {getContentDisplayText(item.text, item.contentUrl, item.audioFileUrl)}
              </Text>
            </div>
          </div>
        </div>

        {/* Consolidated Metadata Row with Audio Player */}
        <div className={styles.searchContentHeader}>
          <div className={styles.searchMetadataRow}>
            {item.topicName && item.topicName !== 'Unknown' && (
              <div className={styles.searchMetadataItem}>
                <span className={styles.searchLabel}>Topic:</span>
                <Badge size={BadgeSize.Small} text={item.topicName} />
              </div>
            )}
            {item.language && item.language.toLowerCase() !== 'unknown' && (
              <div className={styles.searchMetadataItem}>
                <span className={styles.searchLabel}>Language:</span>
                <Badge size={BadgeSize.Small} text={item.language.toUpperCase()} />
              </div>
            )}
            <div className={styles.searchMetadataItem}>
              <span className={styles.searchLabel}>Content Type:</span>
              <Badge size={BadgeSize.Small} text={item.contentType?.toUpperCase() || 'UNKNOWN'} />
            </div>
            {item.labels && item.labels.length > 0 && (
              <div className={styles.searchMetadataItem}>
                <span className={styles.searchLabel}>Labels:</span>
                <div className={styles.searchLabelBadges}>
                  {item.labels.map(label => {
                    const displayText = label.name.toLowerCase() === 'safe' ? label.name.toUpperCase() : `${label.name.toUpperCase()} ${label.severity}`;

                    return <Badge key={`${label.name}-${label.severity}`} size={BadgeSize.Small} text={displayText} />;
                  })}
                </div>
              </div>
            )}
            {item.policyResult && (
              <div className={styles.searchMetadataItem}>
                <span className={styles.searchLabel}>Triggered Policies:</span>
                <PolicyDisplay compact policyResult={item.policyResult} />
              </div>
            )}
          </div>

          {/* Inline Audio Player and Toggle Button - right aligned with metadata */}
          {((item.contentUrl || item.audioFileUrl) && item.contentType?.toLowerCase() === 'audio') || !isSafeContent ? (
            <div className={imageStyles.metadataRightSection}>
              {(item.contentUrl || item.audioFileUrl) && item.contentType?.toLowerCase() === 'audio' && (
                <div className={styles.audioPlayerSection}>
                  <InlineAudioPlayer
                    audioId={item.id}
                    audioUrl={item.contentUrl || item.audioFileUrl || ''}
                    mediaServiceUrl={mediaServiceUrl}
                    transcription={item.text}
                  />
                </div>
              )}
              {!isSafeContent && (
                <IconButton
                  aria-label={isContentBlurred ? 'Show content' : 'Hide content'}
                  buttonSize={IconButtonSize.Small}
                  icon={isContentBlurred ? Eye : StatusCircleBlock}
                  iconSize={IconSize.Small}
                  title={isContentBlurred ? 'Show content' : 'Hide content'}
                  onClick={onToggleBlur}
                />
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export interface ContentCardProps {
  item: ContentCardItem;
  onClick?: () => void;
  mediaServiceUrl?: string;
}

export function ContentCard({ item, onClick, mediaServiceUrl }: ContentCardProps) {
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const isSafeContent = item.classificationResult !== 'rejected';
  const [isContentBlurred, setIsContentBlurred] = useState(!isSafeContent);

  const toggleDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDetailsExpanded(prev => !prev);
  };

  const handleCardClick = () => {
    onClick?.();
  };

  const handleToggleBlur = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsContentBlurred(!isContentBlurred);
  };

  return (
    <Card className={styles.contentCard} onClick={handleCardClick}>
      {/* Search-style content summary with black background and colored border */}
      <SearchStyleContentSummary isContentBlurred={isContentBlurred} item={item} mediaServiceUrl={mediaServiceUrl} onToggleBlur={handleToggleBlur} />

      {/* Expandable details section */}
      <ExpandableDetails isContentBlurred={isContentBlurred} isExpanded={isDetailsExpanded} item={item} onToggleBlur={handleToggleBlur} />

      {/* Centered "Show Details" button like "Show All Decisions" - moves down with expansion */}
      <div className={styles.centeredActionContainer}>
        <Button buttonSize={ButtonSize.Accelerator} buttonType={ButtonType.Tertiary} onClick={toggleDetails}>
          {isDetailsExpanded ? 'Hide Details' : 'Show Details'}
        </Button>
      </div>
    </Card>
  );
}
